// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// Renders the `market_context` slot (Track 1d) — the confirm/nuance/overturn Yardeni
// second-opinion scorecard + regime label + market figures + forward-read panel. Consumes
// get_current_market_context via useMarketContext(). Mirrors GateTab/ArtifactsTab: Outlet
// context for the cycle, LoadingState/ErrorState/EmptyState for the three terminal states,
// CellValue/ProseSlotBlock for the numbers/prose split.
//
// The "no published slot" sentinel is rendered as an HONEST EMPTY STATE, never silently
// swapped for a stale prior-quarter regime (never-fabricate; market-context-slot-v0.md §6).
// Every number on this page is a Cell rendered via CellValue — verdicts/regime/scenarios/
// lean-in are prose, rendered as text, never mined for figures.

import { Link, useOutletContext } from "react-router-dom";
import { CheckCircle2, CircleDot, CircleSlash, Lock, ShieldAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import { LoadingState, ErrorState, EmptyState } from "@/components/research-hub";
import { CellValue, ProseSlotBlock } from "@/components/investment-reporting";
import {
  useMarketContext,
  isMarketContextPublished,
  asCell,
  marketFigureCell,
  type MarketContextScorecardRow,
  type MarketContextProseSlot,
} from "@/hooks/investment-reporting/useMarketContext";
import { isProseSlot } from "@/types/ir";
import type { IR } from "@/types/ir";
import type { CycleContext } from "./ReportingCycleLayout";

const VERDICT_ICON: Record<string, JSX.Element> = {
  confirm: <CheckCircle2 className="h-3.5 w-3.5 text-primary" aria-hidden />,
  nuance: <CircleDot className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" aria-hidden />,
  overturn: <CircleSlash className="h-3.5 w-3.5 text-destructive" aria-hidden />,
};

// Real (non-legacy) scorecard rows are ProseSlot per the slot contract §4 — narrow via the
// shared duck-type check (types/ir.ts) rather than re-implementing it locally.
function isProseSlotRow(row: MarketContextScorecardRow): row is MarketContextProseSlot {
  return isProseSlot(row);
}

/**
 * Link through to the curation surface.
 *
 * `yardeni/curate` is mounted (partnerOpsRoutes.tsx:555) but had NO inbound link anywhere — it was
 * doubly unreachable, since this page was unlinked too. It is the only caller of
 * yardeni_verify_chart_read / yardeni_curate_and_publish, i.e. the sole way to publish the slot
 * this page reads, so this page is its natural home. Relative `to="curate"` resolves against the
 * current /:cycleId/yardeni route, so it needs no cycle id threading. Rendered in BOTH branches
 * below — it matters MOST in the empty state, which is precisely when someone needs to go curate.
 */
function CurateLink() {
  return (
    <Link
      to="curate"
      className="font-body text-xs text-primary underline underline-offset-4 hover:no-underline"
    >
      Curate — verify &amp; publish this slot
    </Link>
  );
}

export default function YardeniSecondOpinion() {
  const { cycle } = useOutletContext<CycleContext>();
  const { data: envelope, isLoading, error, refetch } = useMarketContext(
    cycle.period_label,
    cycle.period_type,
  );

  if (isLoading) return <LoadingState rows={4} />;
  if (error) return <ErrorState onRetry={() => refetch()} />;

  if (!isMarketContextPublished(envelope)) {
    return (
      <div className="space-y-3">
        <EmptyState
          title="No published market_context slot for this period."
          body={
            envelope?.detail ??
            "A human must curate and publish the Yardeni second-opinion slot before it can be reviewed here. Nothing is shown in place of it — never a stale prior-quarter regime."
          }
        />
        <CurateLink />
      </div>
    );
  }

  const slot = envelope.market_context;
  const slotAsIr = slot as unknown as IR; // resolveIrPath only ever indexes into this as a plain object
  // Defensive — this slot is DRAFT 0.2.0 and schema drift is explicitly flagged; degrade to an
  // empty scorecard/figures list rather than crash the whole page on a malformed/partial slot.
  const { counts, rows, overturned } = slot.scorecard ?? {
    counts: { confirm: 0, nuance: 0, overturn: 0 },
    rows: [],
    overturned: [],
  };
  const marketFigures = slot.market_figures ?? [];

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <CurateLink />
      </div>

      {/* Distribution reminder — this slot is IC/adviser only, never the retail newsletter */}
      {slot.distribution?.retail_public === false && (
        <p className="font-body text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
          <Lock className="h-3 w-3" aria-hidden /> IC / adviser only — this slot never reaches the retail newsletter.
        </p>
      )}

      {/* Regime call */}
      <section className="space-y-2">
        <Card className="p-4 space-y-2">
          <div className="font-body text-[11px] uppercase tracking-wide text-muted-foreground">
            Regime · {envelope.run_type === "monthly_delta" ? "monthly delta" : "quarterly full"}
          </div>
          <h3 className="font-heading text-lg font-semibold text-foreground">
            {slot.regime?.message ?? envelope.regime_label ?? "—"}
          </h3>
          <p className="font-body text-xs text-muted-foreground">{slot.regime?.source}</p>
          {envelope.parent_status !== "n/a" && (
            <p className="font-body text-[11px] text-muted-foreground">
              Quarterly base:{" "}
              {envelope.parent_status === "resolved"
                ? "resolved"
                : envelope.parent_status === "missing"
                  ? "no parent linked"
                  : "parent not yet published"}
              {envelope.parent_status !== "resolved" && (
                <span className="text-amber-600 dark:text-amber-400"> — treat this delta as provisional</span>
              )}
            </p>
          )}
          {slot.regime?.callouts?.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2 pt-2 border-t border-border">
              {slot.regime.callouts.map((c, i) => (
                <div key={i} className="font-body text-xs">
                  <div className="font-medium text-foreground">{c.title}</div>
                  <div className="text-muted-foreground">{c.body}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* Scorecard */}
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-heading text-base font-medium text-foreground">Second-opinion scorecard</h3>
          <div className="flex items-center gap-3 font-body text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-primary" aria-hidden /> {counts.confirm} confirm</span>
            <span className="inline-flex items-center gap-1"><CircleDot className="h-3 w-3 text-amber-600 dark:text-amber-400" aria-hidden /> {counts.nuance} nuance</span>
            <span className="inline-flex items-center gap-1"><CircleSlash className="h-3 w-3 text-destructive" aria-hidden /> {counts.overturn} overturn</span>
          </div>
        </div>

        {rows.length === 0 ? (
          <EmptyState title="No scorecard rows in this slot." />
        ) : (
          <div className="space-y-2">
            {rows.map((row, i) =>
              isProseSlotRow(row) ? (
                <div key={i} className="space-y-1">
                  {row.verdict && (
                    <div className="flex items-center gap-2">
                      {VERDICT_ICON[row.verdict]}
                      <span className="font-body text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-muted text-muted-foreground">
                        {row.verdict}
                      </span>
                    </div>
                  )}
                  <ProseSlotBlock slot={row} ir={slotAsIr} />
                </div>
              ) : (
                <Card key={i} className="p-4 space-y-1.5">
                  <div className="flex items-center gap-2">
                    {VERDICT_ICON[row.verdict]}
                    <span className="font-body text-sm font-medium text-foreground">{row.cbam_claim}</span>
                    <span className="font-body text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-muted text-muted-foreground">
                      {row.verdict}
                    </span>
                  </div>
                  <p className="font-body text-xs text-muted-foreground">{row.evidence}</p>
                  <div className="flex items-center gap-2 font-body text-[11px] text-muted-foreground">
                    <span>{row.confidence} confidence</span>
                    {row.adversarially_verified && <span>· skeptic-verified</span>}
                    {row.narrative_ref && <span>· ref: {row.narrative_ref}</span>}
                  </div>
                </Card>
              ),
            )}
          </div>
        )}

        {overturned.length > 0 && (
          <div className="pt-2">
            <h4 className="font-body text-xs font-medium text-destructive mb-1">Overturned</h4>
            <div className="space-y-2">
              {overturned.map((row, i) =>
                isProseSlotRow(row) ? (
                  <div key={i} className="space-y-1">
                    {row.verdict && (
                      <div className="flex items-center gap-2">
                        {VERDICT_ICON[row.verdict]}
                        <span className="font-body text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-muted text-muted-foreground">
                          {row.verdict}
                        </span>
                      </div>
                    )}
                    <ProseSlotBlock slot={row} ir={slotAsIr} />
                  </div>
                ) : (
                  <Card key={i} className="p-3">
                    <p className="font-body text-xs text-foreground">{row.cbam_claim}</p>
                    <p className="font-body text-xs text-muted-foreground">{row.evidence}</p>
                  </Card>
                ),
              )}
            </div>
          </div>
        )}
      </section>

      {/* Market figures — every number here is a Cell via CellValue */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">Market figures</h3>
        <Card className="p-4 divide-y divide-border">
          {marketFigures.length === 0 ? (
            <p className="font-body text-xs text-muted-foreground py-2">No market figures in this slot.</p>
          ) : (
            marketFigures.map((fig, i) => (
              <div key={i} className="py-2 first:pt-0 last:pb-0 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-body text-sm text-foreground">{fig.label}</div>
                  {fig.note && <div className="font-body text-[11px] text-muted-foreground">{fig.note}</div>}
                </div>
                <div className="shrink-0 text-right">
                  <CellValue cell={asCell(marketFigureCell(fig))} signed />
                  {fig.range && <div className="font-body text-[10px] text-muted-foreground">{fig.range}</div>}
                </div>
              </div>
            ))
          )}
        </Card>
      </section>

      {/* Cohorts — pre-formatted annotation text per the slot contract (not Cells) */}
      {slot.cohorts?.length > 0 && (
        <section className="space-y-2">
          <h3 className="font-heading text-base font-medium text-foreground">Cohorts</h3>
          <div className="grid gap-2 sm:grid-cols-3">
            {slot.cohorts.map((c, i) => (
              <Card key={i} className="p-3">
                <div className="font-heading text-lg font-semibold text-foreground">{c.value}</div>
                <div className="font-body text-xs text-muted-foreground">{c.label}</div>
                {c.sub && <div className="font-body text-[11px] text-muted-foreground/70">{c.sub}</div>}
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Forward-read — labelled panel, quarantined cells rendered ONLY here (allowQuarantined) */}
      {slot.forward_read?.stats?.length > 0 && (
        <section className="space-y-2">
          <Card className="p-4 border-amber-500/40 bg-amber-500/5 space-y-2">
            <div className="flex items-center gap-1.5 font-body text-[11px] font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide">
              <ShieldAlert className="h-3.5 w-3.5" aria-hidden /> {slot.forward_read.label}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {slot.forward_read.stats.map((s, i) => (
                <div key={i}>
                  <CellValue cell={asCell(s.value)} allowQuarantined />
                  <div className="font-body text-xs text-foreground">{s.label}</div>
                  {s.sub && <div className="font-body text-[11px] text-muted-foreground">{s.sub}</div>}
                </div>
              ))}
            </div>
          </Card>
        </section>
      )}

      {/* Scenarios */}
      {slot.scenarios?.length > 0 && (
        <section className="space-y-2">
          <h3 className="font-heading text-base font-medium text-foreground">Scenarios</h3>
          <div className="space-y-2">
            {slot.scenarios.map((s, i) => (
              <Card key={i} className="p-3 flex items-start justify-between gap-3 flex-wrap">
                <div className="font-body text-sm text-foreground">{s.scenario}</div>
                <div className="font-body text-xs text-muted-foreground text-right">
                  <div>{s.prob}</div>
                  <div>{s.meaning}</div>
                  <div className="italic">{s.action}</div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Lean-in themes */}
      {slot.lean_in?.length > 0 && (
        <section className="space-y-2">
          <h3 className="font-heading text-base font-medium text-foreground">Lean-in themes</h3>
          <div className="space-y-2">
            {slot.lean_in.map((l, i) => (
              <Card key={i} className="p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-body text-sm font-medium text-foreground">{l.theme}</span>
                  {l.ic_only && (
                    <span className="font-body text-[10px] rounded px-1.5 py-0.5 bg-muted text-muted-foreground">IC only</span>
                  )}
                </div>
                <p className="font-body text-xs text-muted-foreground">{l.value_read}</p>
                <p className="font-body text-[11px] text-muted-foreground">
                  Runway: {l.runway} · Action: {l.cbam_action}
                </p>
              </Card>
            ))}
          </div>
        </section>
      )}

      <p className="font-body text-[11px] text-muted-foreground border-t border-border pt-3">
        Source: {slot.source} · curated {envelope.curated_at ?? "—"} · schema {slot.schema_version}
      </p>
    </div>
  );
}
